/**
 * T841 — booting the offline document safely, wherever the service worker served it.
 *
 * When a network-only navigation fails, the worker's catch handler answers with the
 * precached offline document (`/es/sin-conexion` · `/en/offline`) but the browser keeps
 * the URL she asked for. That document's HTML and hydration data describe the offline
 * route only; booting the router from `location` made it match the URL's route instead,
 * find no data for it and raise `No result found for routeId` — the generic error screen,
 * by itself, the moment her connection came back.
 *
 * The fix reads the document actually on screen, never a new request (a re-fetch proves
 * how a NEW request is served, not how this one was): the offline route renders its own
 * pathname into `<meta name="tfc-offline-fallback">`. Before hydration the client moves
 * the URL to that pathname, carrying the requested URL in `?from=`, so the router boots
 * the route the HTML was rendered for; the offline page then sends her to `from` once the
 * network answers.
 *
 * Pure: no IO. The boot (`entry.client.tsx`) and the recovery hook do the IO.
 */

/** The `<meta name>` the offline route renders; its `content` is the offline pathname. */
export const OFFLINE_FALLBACK_META = "tfc-offline-fallback";

/** The offline route's two paths (`app/routes.ts` ids `offline-es` / `offline-en`). */
export const OFFLINE_PATHS = ["/es/sin-conexion", "/en/offline"] as const;

/**
 * True for the two offline paths, as the router matches them (any case, trailing slash).
 * Root renders them anonymous: the worker precaches them with the device's cookies and
 * serves one copy to whoever holds the device (T885 audit F2).
 */
export function isOfflineFallbackPath(pathname: string): boolean {
  const path = pathname.toLowerCase().replace(/\/+$/, "");
  return (OFFLINE_PATHS as readonly string[]).includes(path);
}

/** The query parameter that carries the URL she asked for while the offline page shows. */
const RECOVERY_PARAM = "from";

/**
 * The namespaces the offline document needs, and the only ones precached with it
 * (`vite.config.ts` `globPatterns` reads this list): `($lang).offline.tsx` declares
 * `error`, root's chrome and boundary add `common`, `nav` and `footer` (R-T52-13), and
 * the components `root.tsx` renders on every page add `consent` (`CookieBanner`), `pwa`
 * (`PwaProvider`) and `maintenance` (`MaintenanceBanner`). Hydrated offline, those render
 * too — without their namespace a new visitor read `REJECT_ALL` on the cookie banner
 * (found at T841 QA). A component added to root's chrome adds its namespace here.
 *
 * Loading every namespace before hydrating is what kept the offline page from hydrating
 * offline: each missing file retried with back-off before `hydrateRoot` ran.
 */
export const OFFLINE_DOCUMENT_NAMESPACES = [
  "error",
  "common",
  "nav",
  "footer",
  "consent",
  "pwa",
  "maintenance",
] as const;

export interface OfflineDocument {
  /** The offline route's own pathname, as the document rendered it. */
  offlinePath: string;
  /** The URL she asked for, when the document sits under another one; else `null`. */
  requested: string | null;
}

type UrlParts = Pick<Location, "pathname" | "search" | "hash">;

/**
 * `null` for any document but the offline one. For the offline document, its pathname and
 * — when the browser shows another URL — the URL she asked for.
 */
export function readOfflineDocument(
  doc: Pick<Document, "querySelector">,
  location: UrlParts,
): OfflineDocument | null {
  const offlinePath = doc.querySelector(`meta[name="${OFFLINE_FALLBACK_META}"]`)?.getAttribute("content");
  if (!offlinePath || sameOriginPath(offlinePath) !== offlinePath) return null;
  const requested = trimSlash(location.pathname) === trimSlash(offlinePath)
    ? null
    : `${location.pathname}${location.search}${location.hash}`;
  return { offlinePath, requested };
}

/** The URL the router boots on: the offline route, carrying where she was going. */
export function offlineBootUrl(offlinePath: string, requested: string): string {
  return `${offlinePath}?${RECOVERY_PARAM}=${encodeURIComponent(requested)}`;
}

/**
 * Where recovery sends her: the `from` parameter, only if it is a same-origin path and not
 * the offline page itself (either locale). Anything else is `null` — an open redirect or a
 * loop is never a recovery.
 */
export function recoveryTarget(search: string, offlinePaths: readonly string[]): string | null {
  const from = sameOriginPath(new URLSearchParams(search).get(RECOVERY_PARAM));
  if (!from) return null;
  const pathname = from.split(/[?#]/, 1)[0];
  // The router matches paths case-insensitively, so `/ES/SIN-CONEXION` is the offline page.
  if (offlinePaths.some((path) => trimSlash(path).toLowerCase() === trimSlash(pathname).toLowerCase())) {
    return null;
  }
  return from;
}

/**
 * At most this many automatic recoveries per target inside the window (R-T841-3: at most
 * one automatic navigation a minute — her decision, 2026-10-06). If that one fails, she stays
 * on the offline page and its retry button takes her to the same target.
 *
 * A record means a recovery that has NOT landed: the client boot clears every record when a
 * normal document boots (`clearOfflineRecoveryRecords`). So a second blip after a landed
 * recovery is free (infra audit round 10), and a navigation that fails, fast or slowly, is
 * never retried automatically inside the minute (round 11) — no clock can tell those apart.
 */
export const MAX_AUTO_RECOVERIES = 1;
const AUTO_RECOVERY_WINDOW_MS = 60_000;

/**
 * The loop guard. A probe can answer and the navigation after it still fail; the worker
 * then serves this page again and recovery would fire again. `attempts` are the epoch
 * milliseconds of earlier automatic recoveries to the same target.
 */
export function mayAutoRecover(attempts: readonly number[], now: number): boolean {
  return attempts.filter((at) => now - at < AUTO_RECOVERY_WINDOW_MS).length < MAX_AUTO_RECOVERIES;
}

/** The gap before the next recovery probe launches: 2 s doubling to 30 s, whatever the last one did. */
export function probeDelayMs(launches: number): number {
  return Math.min(2_000 * 2 ** launches, 30_000);
}

const PROBE_ORIGIN = "https://origin.invalid";

/**
 * The path as the browser will resolve it, or `null` when it leaves the origin. Resolved
 * with the WHATWG parser the navigation itself uses, never pattern-matched: the parser
 * strips tab and newline and reads `\\` as `/`, so `/<TAB>/evil.example` passes any prefix
 * check and still lands on `//evil.example` (T841 QA audit, round 1).
 */
function sameOriginPath(value: string | null): string | null {
  if (!value?.startsWith("/")) return null;
  let url: URL;
  try {
    url = new URL(value, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN) return null;
  // Normalising can itself produce a scheme-relative path (`/..//evil.example` →
  // `//evil.example`, round 2 of the same audit): the RETURNED value must stay on the
  // origin when the browser parses it again.
  const path = `${url.pathname}${url.search}${url.hash}`;
  return new URL(path, PROBE_ORIGIN).origin === PROBE_ORIGIN && !path.startsWith("//") ? path : null;
}

function trimSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}
